import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { CreateBriefService } from '../../../briefs/service/create-brief/create-brief.service';
import { BriefService } from '../../../../services/brief/brief.service';
import { BbField } from '../../ui/bb-field/bb-field';
import { BbChip } from '../../ui/bb-chip/bb-chip';
import { BbSwitch } from '../../ui/bb-switch/bb-switch';
import { FollowerRangeOption, FOLLOWER_RANGES } from '../../brief-builder.constants';

type AgeBracket = { id: number; label: string; minAge: number; maxAge: number };

/** Audience & persona: demographics (reusing the app's real option lists) + creator persona. */
@Component({
  selector: 'bb-audience-section',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [BbField, BbChip, BbSwitch],
  template: `
    <div class="flex flex-col gap-5">
      <bb-field label="Gender">
        <div class="flex flex-wrap gap-2">
          @for (gender of briefService.creatorGenders; track gender.id) {
            <bb-chip [active]="isGenderSelected(gender.id)" (toggled)="toggleGender(gender.id)">{{ gender.name }}</bb-chip>
          }
        </div>
      </bb-field>

      <bb-field label="Age ranges">
        <div class="flex flex-wrap gap-2">
          @for (age of briefService.creatorAges; track age.id) {
            <bb-chip [active]="isAgeSelected(age)" (toggled)="toggleAge(age)">{{ age.label }}</bb-chip>
          }
        </div>
      </bb-field>

      <div class="flex items-center gap-3 py-1">
        <bb-switch [checked]="service.is18plus()" ariaLabel="18+ creators only" (checkedChange)="toggle18Plus($event)" />
        <div>
          <div class="font-heading text-base font-bold text-foreground">18+ creators only</div>
          <div class="text-xs text-muted-foreground">Restrict matching to verified adults.</div>
        </div>
      </div>

      <bb-field label="Interests">
        <div class="flex flex-wrap gap-2">
          @for (interest of briefService.creatorInterests; track interest.id) {
            <bb-chip [active]="isInterestSelected(interest.id)" (toggled)="toggleInterest(interest.id)">{{ interest.name }}</bb-chip>
          }
        </div>
      </bb-field>

      <bb-field
        label="Follower range"
        [hint]="isUgcOnly() ? 'Not applicable for UGC-only briefs.' : ''">
        <div class="flex flex-wrap gap-2" [class.opacity-60]="isUgcOnly()">
          @for (range of followerRanges; track range.label) {
            <bb-chip
              [active]="!isUgcOnly() && isFollowerSelected(range)"
              [disabled]="isUgcOnly()"
              (toggled)="toggleFollower(range)">{{ range.label }}</bb-chip>
          }
        </div>
      </bb-field>

      <bb-field label="Locations">
        <div class="flex flex-wrap gap-2">
          @for (country of briefService.countryCodes; track country.countryCode) {
            <bb-chip [active]="isCountrySelected(country.countryCode)" (toggled)="toggleCountry(country.countryCode)">{{ country.countryName }}</bb-chip>
          }
        </div>
      </bb-field>

      <hr class="border-border" />

      <div class="font-heading text-lg font-bold text-foreground">Creator persona</div>
      <bb-field label="Who's the ideal creator?">
        <textarea
          [value]="persona().creator_persona"
          (input)="updatePersona('creator_persona', $any($event.target).value)"
          rows="3"
          placeholder="e.g. Lifestyle creators who film authentic day-in-the-life content…"
          class="w-full resize-y rounded-md border border-border bg-card px-3.5 py-2.5 text-base text-foreground outline-none focus:border-brand-studio focus:ring-2 focus:ring-brand-studio/25 placeholder:text-muted-foreground"></textarea>
      </bb-field>
      <div class="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <bb-field label="Their audience">
          <textarea
            [value]="persona().creator_audience"
            (input)="updatePersona('creator_audience', $any($event.target).value)"
            rows="2"
            placeholder="e.g. Young professionals interested in wellness and productivity…"
            class="w-full resize-y rounded-md border border-border bg-card px-3.5 py-2.5 text-base text-foreground outline-none focus:border-brand-studio focus:ring-2 focus:ring-brand-studio/25 placeholder:text-muted-foreground"></textarea>
        </bb-field>
        <bb-field label="Content style">
          <textarea
            [value]="persona().creator_content_style"
            (input)="updatePersona('creator_content_style', $any($event.target).value)"
            rows="2"
            placeholder="e.g. Natural, unpolished talking-head videos with on-screen text…"
            class="w-full resize-y rounded-md border border-border bg-card px-3.5 py-2.5 text-base text-foreground outline-none focus:border-brand-studio focus:ring-2 focus:ring-brand-studio/25 placeholder:text-muted-foreground"></textarea>
        </bb-field>
      </div>
    </div>
  `,
})
export class AudienceSection {
  readonly service = inject(CreateBriefService);
  readonly briefService = inject(BriefService);
  readonly followerRanges: FollowerRangeOption[] = FOLLOWER_RANGES;
  readonly isUgcOnly = computed(() => this.service.selectedUgcOnly());

  private readonly emptyPersona = {
    creator_persona: '',
    creator_audience: '',
    creator_content_style: '',
    creator_engagement_metrics: '',
    creator_specific_characteristics: '',
    search_string: '',
  };

  persona() {
    return this.service.creatorProfileModel() ?? this.emptyPersona;
  }

  updatePersona(key: 'creator_persona' | 'creator_audience' | 'creator_content_style', value: string): void {
    const current = this.service.creatorProfileModel() ?? { ...this.emptyPersona };
    this.service.creatorProfileModel.set({ ...current, [key]: value });
  }

  isGenderSelected(id: number): boolean {
    const selected = this.service.selectedGenders();
    if (selected.length === 0 && id === 0) {
      return true;
    }
    return selected.includes(id);
  }

  toggleGender(id: number): void {
    this.service.selectedGenders.update((genders) =>
      genders.includes(id) ? genders.filter((g) => g !== id) : [...genders, id],
    );
  }

  isAgeSelected(age: AgeBracket): boolean {
    return this.service.selectedAge().some((a) => a.id === age.id);
  }

  toggleAge(age: AgeBracket): void {
    this.service.selectedAge.update((ages) =>
      ages.some((a) => a.id === age.id) ? ages.filter((a) => a.id !== age.id) : [...ages, age],
    );
  }

  toggle18Plus(next: boolean): void {
    this.service.is18plus.set(next);
    if (next) {
      const kidBracket = this.briefService.creatorAges[0];
      if (kidBracket) {
        this.service.selectedAge.update((ages) => ages.filter((a) => a.id !== kidBracket.id));
      }
    }
  }

  isInterestSelected(id: number | string): boolean {
    return this.service.selectedInterests().includes(Number(id));
  }

  toggleInterest(id: number | string): void {
    const numId = Number(id);
    this.service.selectedInterests.update((interests) =>
      interests.includes(numId) ? interests.filter((i) => i !== numId) : [...interests, numId],
    );
  }

  isFollowerSelected(range: FollowerRangeOption): boolean {
    return this.service.selectedFollowerRanges().some((r) => r.min === range.min && r.max === range.max);
  }

  toggleFollower(range: FollowerRangeOption): void {
    if (this.isUgcOnly()) {
      return;
    }
    this.service.selectedFollowerRanges.update((ranges) =>
      ranges.some((r) => r.min === range.min && r.max === range.max)
        ? ranges.filter((r) => !(r.min === range.min && r.max === range.max))
        : [...ranges, { min: range.min, max: range.max }],
    );
  }

  isCountrySelected(code: string): boolean {
    return this.service.selectedCountryCodes().includes(code);
  }

  toggleCountry(code: string): void {
    this.service.selectedCountryCodes.update((codes) =>
      codes.includes(code) ? codes.filter((c) => c !== code) : [...codes, code],
    );
  }
}
